import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import MediaListing from '@/components/common/media/MediaListing';
import PageHeading from '@/components/common/PageHeading';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import { IBrandsResponse } from '@/interfaces/brandsResponse.interface';
import { ICategoryData } from '@/interfaces/categoryResponse.interface';
import { IproductResponse } from '@/interfaces/productresponse.interface';
import {
  checkIsExpire,
  formatDateTime,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { Star } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

interface StatesProp {
  isLoading: boolean;
  productInfo: IproductResponse | null;
  categoryInfo: ICategoryData | null;
  brandInfo: IBrandsResponse | null;
}
const ProductDetail: React.FC = () => {
  const { id } = useParams();
  const productId = id;
  const breadcrumb = [
    { label: 'Products', href: RoutesLink.products },
    { label: 'Details' },
  ];
  const [states, setStates] = useState<StatesProp>({
    isLoading: false,
    productInfo: null,
    categoryInfo: null,
    brandInfo: null,
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchProductInfo = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${productId}`;
      const res = await callApi<IproductResponse>(PATH, { method: 'GET' });
      console.log('res is', res);
      handleChangeState({
        productInfo: res,
        isLoading: false,
      });
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in fetching product info', error);
    }
  };

  const fetchCategoryBrand = async () => {
    try {
      const API_URL = `${API_URL_PRODUCT_SERVICE}`;
      const CATPATH = `${API_URL}${API_ENDPOINT.categories}/${states.productInfo?.categoryId}`;
      const BRANDPATH = `${API_URL}${API_ENDPOINT.brands}/${states.productInfo?.brandId}`;
      const [brands, category] = await Promise.all([
        callApi(BRANDPATH, { method: 'GET' }),
        callApi(CATPATH, { method: 'GET' }),
      ]);
      handleChangeState({ brandInfo: brands, categoryInfo: category });
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in fetching product info', error);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchProductInfo();
    }
  }, [productId]);

  useEffect(() => {
    if (states.productInfo) fetchCategoryBrand();
  }, [states.productInfo]);

  const productBasicInfo = useMemo(
    () => [
      { label: 'Name', value: states.productInfo?.name },
      { label: 'Item Id', value: states.productInfo?.inventoryItemId },
      { label: 'Item Code', value: states.productInfo?.itemCode },
      { label: 'Description', value: states.productInfo?.description },
      { label: 'Category', value: `${states.categoryInfo?.name || '--'}` },
      { label: 'Brand', value: states.brandInfo?.name },
    ],
    [states.productInfo, states.brandInfo, states.categoryInfo],
  );

  const productPricing = useMemo(
    () => [
      {
        label: 'Price',
        value: `BD ${states.productInfo?.productInfo[0]?.secondaryPrice || states.productInfo?.productInfo[0]?.price}`,
      },
      {
        label: 'Loyalty Points',
        value: states.productInfo?.productInfo[0]?.loyaltyPoints ?? 0,
      },
      {
        label: 'Stocks',
        value: states.productInfo?.productInfo[0]?.secondaryQuantity,
      },
      {
        label: 'Vat',
        value: states.productInfo?.productInfo[0]?.isVatIncluded
          ? 'Included'
          : 'Not Included',
      },
      {
        label: 'Weight',
        value: states.productInfo?.productInfo[0]?.secondaryPacking,
      },
      { label: 'UOM', value: states.productInfo?.productInfo[0]?.secondaryUOM },
    ],
    [states.productInfo],
  );

  const metaType = useMemo(
    () => states.productInfo?.productInfo[0]?.meta,
    [states.productInfo],
  );

  const refundPolicy = useMemo(
    () => [
      {
        label: 'Refundable',
        value: `${metaType?.refund.isRefundable ? 'Yes' : 'No'}`,
      },
      { label: 'Refundable Days', value: metaType?.refund.window },
    ],
    [metaType],
  );

  const discounts = useMemo(
    () => [
      {
        label: 'Discount %',
        value: states.productInfo?.productInfo[0]?.discountPercentage,
      },
      {
        label: 'Discounted Price',
        value: states.productInfo?.productInfo[0]?.discountedPrice,
      },
      {
        label: 'Start Date',
        value: formatDateTime(
          states.productInfo?.productInfo[0]?.discountStartDate,
          'YYMMMDDHH',
        ),
      },
      {
        label: 'End Date',
        value: formatDateTime(
          states.productInfo?.productInfo[0]?.discountEndDate,
        ),
      },
    ],
    [states.productInfo],
  );

  const productAddons = states.productInfo?.productInfo[0]?.addons;

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item.media[0].url}`}
          alt=""
          text={item.name}
        />
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (item) => {
        const price =
          item.productInfo?.[0]?.secondaryPrice ||
          item.productInfo?.[0]?.price ||
          0;
        return <span>{toFixedThree(price)}</span>;
      },
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'Refundable',
      header: 'Refundable',
      render: (item) =>
        item.productInfo[0]?.meta?.refund?.isRefundable ? 'Yes' : 'No',
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item.averageRating || 0, 1)}{' '}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Breadcrumb items={breadcrumb} />
        </div>
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
        >
          <DetailsCard title="Product Info" details={productBasicInfo} />
          <DetailsCard title="Pricing & Packaging" details={productPricing} />

          <DetailsCard title="Refund Policy" details={refundPolicy} />
          <DetailsCard title="Discounts" details={discounts} />
        </div>
        {productAddons && productAddons.length > 0 && (
          <Card>
            <PageHeading text="Add Ons" />
            <CommonTable columns={columns} data={productAddons} />
          </Card>
        )}
        <Card>
          <h3 className="text-base text-black mb-5">Product Images</h3>
          <div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mt-2"
              uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
              {states?.productInfo?.directImageUrl ? (
                <>
                  <MediaListing
                    BaseUrl={API_URL_PRODUCT_SERVICE}
                    mediaUrl={states?.productInfo?.directImageUrl}
                  />
                </>
              ) : (
                states?.productInfo?.media.map((item) => {
                  return (
                    <MediaListing
                      BaseUrl={API_URL_PRODUCT_SERVICE}
                      mediaUrl={item?.url}
                    />
                  );
                })
              )}
            </div>
            {/* <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mt-2"
              uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
              <div className="w-full lg:h-60 h-full aspect-[3/3] bg-slate-200/60 rounded-lg dark:bg-dark2 animate-pulse"></div>
              <div className="w-full lg:h-60 h-full aspect-[3/3] bg-slate-200/60 rounded-lg dark:bg-dark2 animate-pulse"></div>
              <div className="w-full lg:h-60 h-full aspect-[3/3] bg-slate-200/60 rounded-lg dark:bg-dark2 animate-pulse"></div>
              <div className="w-full lg:h-60 h-full aspect-[3/3] bg-slate-200/60 rounded-lg dark:bg-dark2 animate-pulse"></div>
            </div> */}
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ProductDetail;
